import { useState, useEffect, useRef, Fragment } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import { useSession } from "@/components/providers/session";
import { Dialog } from "@headlessui/react";
import {
  Bars3Icon,
  UserIcon,
  XMarkIcon,
  ShoppingBagIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import dynamic from "next/dynamic";
import OnlineBookingCheckout from "@/components/checkout/panel";
import DesktopMenu from "./mega-menu/desktop-menu";
import MobileMenu from "./mega-menu/mobile-menu";
import { trackOfflineSource } from "@/utils/track-offline-source";
import { MegaMenuConfig } from "./mega-menu/types";
import { megaMenuConfig } from "@/config/mega-menu";
import {
  ONLINE_BOGOF_BANNER_HEADLINE,
  shouldShowOnlineBogofNavBanner,
} from "@/lib/online-course-bogof";
import {
  CAREER_SPONSORSHIP_BANNER_HEADLINE,
  CAREER_SPONSORSHIP_BANNER_SUBHEAD,
  CAREER_SPONSORSHIP_PAGE_PATH,
  isCareerSponsorshipPromotionVisible,
} from "@/utils/career-sponsorship-fund-2026";

const HeaderSearch = dynamic(() => import("./organisms/header-search"));

export default function Navigation({ siteData, ...rest }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountPanelRef = useRef<HTMLDivElement>(null);
  const session = useSession();
  const router = useRouter();
  const menus: MegaMenuConfig[] = megaMenuConfig.menus;

  // Determine active menu based on current route
  const getActiveMenuId = (): string | null => {
    const path = router.asPath;
    for (const menu of menus) {
      if (path.startsWith(menu.url) || path === menu.url) {
        return menu.id;
      }
      // Check if any link in the menu matches
      if (menu.linkGroups) {
        for (const group of menu.linkGroups) {
          for (const item of group.items) {
            if (path.startsWith(item.url) || path === item.url) {
              return menu.id;
            }
          }
        }
      }
    }
    return null;
  };

  const activeMenuId = getActiveMenuId();

  // Close mobile menu on route change
  useEffect(() => {
    const handleRouteChange = () => {
      setOpen(false);
      setAccountMenuOpen(false);
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events]);

  // Account menu hover handlers
  const handleAccountMenuEnter = () => {
    if (accountMenuTimeoutRef.current) {
      clearTimeout(accountMenuTimeoutRef.current);
      accountMenuTimeoutRef.current = null;
    }
    setAccountMenuOpen(true);
  };

  const handleAccountMenuLeave = () => {
    accountMenuTimeoutRef.current = setTimeout(() => {
      setAccountMenuOpen(false);
    }, 200);
  };

  // Close account menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        accountMenuOpen &&
        accountMenuRef.current &&
        !accountMenuRef.current.contains(e.target as Node) &&
        accountPanelRef.current &&
        !accountPanelRef.current.contains(e.target as Node)
      ) {
        setAccountMenuOpen(false);
      }
    };

    if (accountMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (accountMenuTimeoutRef.current) {
        clearTimeout(accountMenuTimeoutRef.current);
      }
    };
  }, [accountMenuOpen]);

  const handleSignOut = async () => {
    await session.logout();
  };

  return (
    <>
      <div className="bg-white relative z-50">
        {/* Mobile menu */}
        <Dialog
          open={open}
          onClose={setOpen}
          className="relative z-50 lg:hidden"
        >
          <Dialog.Backdrop className="fixed inset-0 bg-black/25 transition-opacity duration-300 ease-linear opacity-1 z-40" />

          <div className="fixed inset-0 z-40 flex">
            <Dialog.Panel className="relative flex w-full max-w-xs transform flex-col overflow-y-auto bg-white pb-12 shadow-xl transition duration-300 ease-in-out data-[closed]:-translate-x-full">
              <div className="flex px-4 pb-2 pt-5">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="-m-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400"
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon aria-hidden="true" className="size-6" />
                </button>
              </div>

              <div className="space-y-6 border-b border-gray-200 px-4 py-6">
                <a
                  href="tel:+443330155117"
                  onClick={() => trackOfflineSource("phone", "nav-mobile")}
                  className="block w-full md:w-max rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500 text-center"
                >
                  <span className="relative z-[1] text-center">
                    Call Us: 0333 015 5117
                  </span>
                </a>
                <Link
                  href="/request-a-callback"
                  className="block w-full md:w-max group bg-white rounded-md text-sm font-semibold px-6 py-4 leading-6 eading-6 text-gray-600 ring-2 ring-gray-900/10 hover:ring-brand-600 shadow-lg  transition-all duration-200 hover:shadow-lg hover:shadow-brand-200 text-center"
                >
                  Speak With An Advisor{" "}
                  <span
                    aria-hidden="true"
                    className="group-hover:pl-1 transition-all duration-150"
                  >
                    →
                  </span>
                </Link>
              </div>

              {/* Mobile Menu */}
              <div className="mt-2">
                <MobileMenu menus={menus} />
              </div>

              <div className="border-t border-gray-200 px-4 py-6"></div>
            </Dialog.Panel>
          </div>
        </Dialog>

        <header className="relative bg-white">
          {/* <nav
            aria-label="Top"
            className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 hidden lg:block"
          >
            <div className="border-b border-gray-200">
              <div className="flex h-16 items-center justify-between w-full">
                <div className="block flex-1 self-stretch w-full">
                  <div className="flex h-full space-x-6 justify-between w-full">
                    <a
                      href="/pathway-to-aesthetics/"
                      className="flex items-center text-sm font-bold text-gray-700 hover:text-gray-800 bg-clip-text bg-no-repeat text-transparent bg-gradient-to-r py-4 from-brand-500 via-violet-500 to-brand-500 [text-shadow:0_0_rgba(0,0,0,0.1)]"
                    >
                      Pathway to Aesthetics: Level 3 to 7 Training
                    </a>
                    <a
                      href="/courses/packages/"
                      className="flex items-center text-sm font-bold text-gray-700 hover:text-gray-800 bg-clip-text bg-no-repeat text-transparent bg-gradient-to-r py-4 from-brand-500 via-violet-500 to-brand-500 [text-shadow:0_0_rgba(0,0,0,0.1)]"
                    >
                      Exclusive Training Packages
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </nav> */}
          <nav
            aria-label="Top"
            className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative"
          >
            <div className="border-b border-gray-200">
              <div className="flex h-16 items-center justify-between">
                <div className="flex flex-1 items-center lg:hidden">
                  <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="-ml-2 rounded-md bg-white p-2 text-gray-400"
                  >
                    <span className="sr-only">Open menu</span>
                    <Bars3Icon aria-hidden="true" className="size-6" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setSearch(true)}
                    className="ml-2 p-2 text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Search</span>
                    <MagnifyingGlassIcon
                      aria-hidden="true"
                      className="size-6"
                    />
                  </button>
                </div>

                {/* Logo */}
                <a href="/" className="flex">
                  <span className="sr-only">Cosmetic College</span>
                  <Image
                    src="/logo/cc-logo.png"
                    alt="The Cosmetic College"
                    width={135}
                    height={25}
                    className="hidden md:block"
                  />
                  <Image
                    src="/logo/icon.png"
                    alt="The Cosmetic College"
                    width={34}
                    height={34}
                    className="block md:hidden"
                  />
                </a>

                {/* Header Utilities - Right Side */}
                <div className="flex flex-1 items-center justify-end">
                  {/* Phone */}
                  <a
                    href="tel:+443330155117"
                    onClick={() => trackOfflineSource("phone", "nav-header")}
                    className="ml-6 p-2 text-gray-600 hover:text-gray-800 flex items-center"
                  >
                    <span className="sr-only">Call Us</span>
                    <PhoneIcon aria-hidden="true" className="size-4 mr-1" />
                    <span className="hidden md:block tracking-tight text-sm font-medium">
                      0333 015 5117
                    </span>
                  </a>

                  <a
                    href="/request-a-callback/"
                    className="ml-6 p-2 text-gray-600 hover:text-gray-800 items-center hidden lg:flex"
                  >
                    <span className="sr-only">Call Us</span>
                    <ChatBubbleLeftRightIcon
                      aria-hidden="true"
                      className="size-4 mr-1"
                    />
                    <span className="hidden md:block tracking-tight text-sm font-medium">
                      Meet With An Advisor
                    </span>
                  </a>

                  {/* Search */}
                  <button
                    type="button"
                    onClick={() => setSearch(!search)}
                    className="ml-6 hidden p-2 text-gray-600 hover:text-gray-500 lg:block"
                  >
                    <span className="sr-only">Search</span>
                    <MagnifyingGlassIcon
                      aria-hidden="true"
                      className="size-5"
                    />
                  </button>

                  {/* Account */}
                  {session.user && !session.loading ? (
                    <div
                      ref={accountMenuRef}
                      className="relative lg:ml-4"
                      onMouseEnter={handleAccountMenuEnter}
                      onMouseLeave={handleAccountMenuLeave}
                    >
                      <a
                        href="/account/"
                        className="p-2 text-gray-600 hover:text-gray-500 block"
                        aria-expanded={accountMenuOpen}
                        aria-controls="account-menu"
                      >
                        <span className="sr-only">Account</span>
                        <UserIcon aria-hidden="true" className="size-5" />
                      </a>

                      {/* Account Dropdown */}
                      <div
                        ref={accountPanelRef}
                        id="account-menu"
                        role="menu"
                        aria-label="Account menu"
                        className={`absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 transition-all duration-200 ease-out ${
                          accountMenuOpen
                            ? "opacity-100 visible pointer-events-auto"
                            : "opacity-0 invisible pointer-events-none"
                        }`}
                        onMouseEnter={handleAccountMenuEnter}
                        onMouseLeave={handleAccountMenuLeave}
                        aria-hidden={!accountMenuOpen}
                      >
                        <a
                          href="/account/"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                          role="menuitem"
                        >
                          <UserIcon
                            className="size-4 mr-3"
                            aria-hidden="true"
                          />
                          View Account
                        </a>
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                          role="menuitem"
                        >
                          <ArrowRightOnRectangleIcon
                            className="size-4 mr-3"
                            aria-hidden="true"
                          />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  ) : (
                    <a
                      href="/account/"
                      className="p-2 text-gray-600 hover:text-gray-500 lg:ml-4"
                    >
                      <span className="sr-only">Account</span>
                      <UserIcon aria-hidden="true" className="size-5" />
                    </a>
                  )}

                  {/* Cart */}
                  <div className="ml-4 flow-root lg:ml-6">
                    {session.selectedCourses &&
                    session.selectedCourses.length > 0 ? (
                      <Link href="/checkout/" className="flex mr-2">
                        <div className="relative h-8 w-8 rounded-full text-brand-600 flex justify-center items-center hover:text-gray-200">
                          {session.selectedCourses &&
                            session.selectedCourses.length && (
                              <div className="absolute -top-1 -left-1 w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-[10px] font-semibold">
                                {session.selectedCourses.length}
                              </div>
                            )}
                          <ShoppingBagIcon
                            aria-hidden="true"
                            className="size-5 shrink-0 text-gray-600 group-hover:text-gray-500"
                          />
                        </div>
                      </Link>
                    ) : session.onlineCourses.length > 0 ? (
                      <button
                        type="button"
                        onClick={() =>
                          session.setContext((s) => ({
                            ...s,
                            onlineBasket: !s.onlineBasket,
                          }))
                        }
                        className="flex mr-2"
                      >
                        <div className="relative h-8 w-8 rounded-full text-brand-600 flex justify-center items-center hover:text-gray-200">
                          {session.onlineCourses &&
                            session.onlineCourses.length && (
                              <div className="absolute -top-1 -left-1 w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-[10px] font-semibold">
                                {session.onlineCourses.length}
                              </div>
                            )}
                          <ShoppingBagIcon
                            aria-hidden="true"
                            className="size-5 shrink-0 text-gray-600 group-hover:text-gray-500"
                          />
                        </div>
                      </button>
                    ) : (
                      <Link href="/checkout/" className="flex mr-2">
                        <div className="relative h-8 w-8 rounded-full text-brand-600 flex justify-center items-center hover:text-gray-200">
                          {session.selectedCourses &&
                            session.selectedCourses.length && (
                              <div className="absolute -top-1 -left-1 w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center text-[10px] font-semibold">
                                {session.selectedCourses.length}
                              </div>
                            )}
                          <ShoppingBagIcon
                            aria-hidden="true"
                            className="size-6 shrink-0 text-gray-400 group-hover:text-gray-500"
                          />
                        </div>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </nav>
        </header>
      </div>
      {isCareerSponsorshipPromotionVisible() &&
      !router.asPath.startsWith(CAREER_SPONSORSHIP_PAGE_PATH) ? (
        <div className="sticky top-0 z-40 border-b border-amber-200 bg-amber-50/95 backdrop-blur">
          <div className="mx-auto max-w-7xl px-4 py-2 sm:px-6 lg:px-8">
            <Link
              href={`${CAREER_SPONSORSHIP_PAGE_PATH}/`}
              className="block text-center text-sm font-semibold text-amber-900 hover:text-amber-950"
            >
              <span className="block sm:inline">
                {CAREER_SPONSORSHIP_BANNER_HEADLINE}
              </span>
              <span className="mt-0.5 block text-xs font-medium sm:mt-0 sm:ml-2 sm:inline sm:text-sm">
                {CAREER_SPONSORSHIP_BANNER_SUBHEAD}
              </span>
            </Link>
          </div>
        </div>
      ) : shouldShowOnlineBogofNavBanner(router.asPath) &&
        !isCareerSponsorshipPromotionVisible() ? (
        <div className="sticky top-0 z-40 border-b border-amber-200 bg-amber-50/95 backdrop-blur">
          <div className="mx-auto max-w-7xl px-4 py-2 sm:px-6 lg:px-8">
            <Link
              href="/courses/elearning/"
              className="block text-center text-sm font-semibold text-amber-900 hover:text-amber-950"
            >
              {ONLINE_BOGOF_BANNER_HEADLINE}
              <span className="hidden sm:inline">
                {" "}
                - lowest priced course free
              </span>
            </Link>
          </div>
        </div>
      ) : null}
      <HeaderSearch show={search} close={() => setSearch(false)} />

      {/* Desktop Mega Menus */}
      <div className="hidden lg:block lg:flex-1 lg:self-stretch relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 w-full">
        <div className="flex h-full space-x-6">
          {menus.map((menu) => (
            <DesktopMenu
              key={menu.id}
              menu={menu}
              isActive={activeMenuId === menu.id}
            />
          ))}
        </div>
      </div>
      <div className="sr-only">
        {siteData?.fields?.menu?.links?.map((x) => {
          return (
            <Fragment key={x.link ?? x.text}>
              <p>
                <a href={x.link}>{x.text}</a>
              </p>
              {x.children
                ? x.children.map((y) => {
                    return (
                      <p key={y.link ?? y.text}>
                        <a href={y.link}>{y.text}</a>
                        {y.description ? <span>{y.description}</span> : null}
                      </p>
                    );
                  })
                : null}
            </Fragment>
          );
        })}
      </div>
      <OnlineBookingCheckout
        {...rest}
        open={session.onlineBasket}
        setOpen={(s) => {
          session.setContext({ ...session, onlineBasket: s });
        }}
      />
    </>
  );
}
